import {
  Box,
  Checkbox,
  Divider,
  Fade,
  InputAdornment,
  makeStyles,
  Popover,
  PopoverOrigin,
  PopoverProps,
  TextField,
} from '@material-ui/core';

import ControlPointIcon from '@material-ui/icons/ControlPoint';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import SearchIcon from '@material-ui/icons/Search';
import { ReportCAPIEventForm } from '@src/components/CAPI/ReportCAPIEventPopover';
import { FormSubmitEventHandler } from '@src/components/UI/Form';
import usePopoverState from '@src/utils/usePopoverState';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import TagLabel from './TagLabel';
import { AnyTag, ConversationTag } from './types';
import { getTagId, getTagName, positions } from './utils';

const useStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.primary.main,
  },
  buttonColorPrimary: {
    color: theme.palette.primary.main,
  },
  buttonColorGray: {
    color: theme.palette.grey[400],
  },
}));

export interface IAddTag {
  label?: React.ReactNode;
  title: React.ReactNode;
  placeholder?: string;
  tags: AnyTag[];
  handleUpdateTag: <T extends AnyTag>(
    tag: T,
    checked: boolean,
    capiEvent: boolean
  ) => void | Promise<void>;
  position?: 'left' | 'under' | 'over' | 'overRight';
  bgColor?: string;
  grayButton?: boolean;
  PopoverProps?: Partial<PopoverProps>;
  currentCAPIEvents: string[];
  enableCAPIEvents: boolean;
}

const AddTag: React.FC<IAddTag> = ({
  tags,
  label,
  title,
  placeholder,
  handleUpdateTag,
  position = 'under',
  grayButton = false,
  currentCAPIEvents,
  enableCAPIEvents,
  // eslint-disable-next-line no-shadow
  PopoverProps,
}) => {
  const popover = usePopoverState();
  const [searchPattern, setSearchPattern] = useState('');
  const { root, buttonColorGray, buttonColorPrimary } = useStyles();

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        fontWeight={500}
        whiteSpace="nowrap"
        className={root}
        style={{ gap: '8px', cursor: 'pointer' }}
        onClick={event => popover.setAnchorEl(event.currentTarget)}
      >
        {label}
        <ControlPointIcon
          className={grayButton ? buttonColorGray : buttonColorPrimary}
          fontSize="small"
        />
      </Box>
      <Popover
        anchorEl={popover.anchorEl}
        open={popover.isOpen}
        onClose={popover.close}
        elevation={0}
        TransitionComponent={Fade}
        anchorOrigin={positions[position].anchorOrigin as PopoverOrigin}
        transformOrigin={positions[position].transformOrigin as PopoverOrigin}
        {...PopoverProps}
      >
        <Box
          p="8px 7px"
          style={{
            borderRadius: '8px',
          }}
          minWidth={241}
          maxWidth={302}
        >
          <Box mt={0.7} mb={0.5} fontSize="0.9rem" fontWeight={600}>
            {title}
          </Box>
          <TextField
            placeholder={placeholder}
            variant="outlined"
            onChange={event => setSearchPattern(event.target.value)}
            value={searchPattern}
            style={{
              margin: '8px 0px',
              color: 'inherit',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalOfferOutlinedIcon
                    fontSize="small"
                    style={{ color: 'inherit' }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" style={{ color: 'inherit' }} />
                </InputAdornment>
              ),
              style: { height: '34px', paddingBlock: 0 },
            }}
          />
          <Divider style={{ marginInline: '-8px' }} />
          <Box
            minHeight={50}
            maxHeight={382}
            overflow="auto"
            display="flex"
            flexDirection="column"
            pr={2}
            paddingTop={1}
            gridGap={8}
          >
            {tags
              .filter(t => getTagName(t).includes(searchPattern))
              .map(tag =>
                !enableCAPIEvents ||
                !tag.conversionType ||
                currentCAPIEvents?.includes(tag.conversionType || '') ? (
                  <TagMenuItem
                    key={getTagId(tag)}
                    tag={tag}
                    onChange={(_, checked) => {
                      handleUpdateTag(tag, checked, false);
                    }}
                  />
                ) : (
                  <CapiTagMenuItem
                    key={getTagId(tag)}
                    tag={tag as ConversationTag}
                    onChange={(capiTag, checked) =>
                      handleUpdateTag(capiTag, checked, true)
                    }
                  />
                )
              )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default AddTag;

type TagMenuItemProps = {
  tag: AnyTag;
  disabled?: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
};

const TagMenuItem: React.FC<TagMenuItemProps> = ({
  tag,
  onChange,
  disabled,
}) => (
  <Box
    key={getTagId(tag)}
    display="flex"
    flexDirection="row"
    alignItems="center"
    justifyContent="space-between"
  >
    <TagLabel {...tag} size="small" ellipsis />
    <Checkbox
      color="primary"
      size="small"
      style={{
        padding: 0,
      }}
      checked={tag.assigned}
      onChange={onChange}
      disabled={disabled}
    />
  </Box>
);

type CapiTagMenuItemProps = {
  tag: ConversationTag;
  onChange: (capiTag: ConversationTag, checked: boolean) => void;
};
const CapiTagMenuItem: React.FC<CapiTagMenuItemProps> = ({ tag, onChange }) => {
  const defaultValues = useMemo(
    () => ({
      event: tag.conversionType || '',
      currency: '',
      amount: { value: '0', valueAsNumber: 0 },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const form = useForm({ defaultValues });
  const onSubmitHandler: FormSubmitEventHandler<typeof defaultValues> = (
    values,
    isValid
  ) => {
    if (isValid) {
      tag.assigned = true;
      onChange(
        {
          ...tag,
          ...(tag.conversionType === 'PURCHASE' && {
            purchase: {
              currency: values.currency,
              value: values.amount.valueAsNumber,
            },
          }),
        },
        tag.assigned
      );
      popover.close();
    }
  };
  const popover = usePopoverState();

  return (
    <>
      <TagMenuItem
        tag={tag}
        onChange={(event, checked) => {
          if (checked) {
            popover.open(event.currentTarget);
          } else {
            tag.assigned = false;
            onChange(tag, false);
          }
        }}
      />
      <Popover
        open={popover.isOpen}
        onClose={popover.close}
        anchorEl={popover.anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <ReportCAPIEventForm
          form={form}
          onSubmit={onSubmitHandler}
          eventFieldReadOnly
        />
      </Popover>
    </>
  );
};
